import { styled } from '@glitz/react';
import * as React from 'react';
import { isNordicNest } from 'webapp/theme';
import { paleBlack } from 'webapp/shared/sharedTheme';
import { CircleFilledIcon, CircleIcon } from '../icons';
import type { EmblaCarouselType } from 'embla-carousel';
import { ResetButton } from '../button/button';
import { translate } from '@avensia/excite';

type PropType = {
  emblaApi: EmblaCarouselType | undefined;
};

export const DotButtons = ({ emblaApi }: PropType) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

  const onDotButtonClick = React.useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = React.useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = React.useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  React.useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return scrollSnaps.length > 1 ? (
    <Dots>
      {scrollSnaps.map((_, index) => (
        <li key={index}>
          <ResetButton
            aria-label={`${translate(t => t.goToSlide, { index: index + 1 })}${
              index === selectedIndex ? `. ` + translate(t => t.currentSlide) : ''
            }`}
            type="button"
            onClick={() => onDotButtonClick(index)}
          >
            {index === selectedIndex ? <DotCurrent aria-hidden="true" /> : <Dot aria-hidden="true" />}
          </ResetButton>
        </li>
      ))}
    </Dots>
  ) : (
    <></>
  );
};

const dotDecorator = styled({
  cursor: 'pointer',
  width: 15,
  height: 15,
  color: isNordicNest ? 'inherit' : paleBlack,
});

const Dot = styled(CircleIcon, dotDecorator);

const DotCurrent = styled(CircleFilledIcon, dotDecorator);

export const Dots = styled.ol({
  listStyleType: 'none',
  display: 'flex',
  justifyContent: 'center',
  gap: t => t.sharedSpacing.s,
  padding: { top: t => t.sharedSpacing.l, left: 0 },
});
